import React, { useState, useEffect } from 'react';
import { FaUnlock, FaUserSlash, FaSearch } from 'react-icons/fa';
import api from '../../services/api';
import { showError, showSuccess } from '../../utils/toast';
import SkeletonLoader from '../common/SkeletonLoader';

const BlockedUsers = () => {
    const [blockedUsers, setBlockedUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    useEffect(() => {
        fetchBlockedUsers();
    }, []);

    const fetchBlockedUsers = async () => {
        setLoading(true);
        try {
            const res = await api.get('/blocked-users');
            setBlockedUsers(res.data);
        } catch (error) {
            console.error(error);
            showError('Failed to fetch blocked users');
        } finally {
            setLoading(false);
        }
    };

    const handleUnblock = async (userId) => {
        try {
            await api.post('/unblock', { userId });
            showSuccess('User unblocked successfully');
            setBlockedUsers(blockedUsers.filter(u => u._id !== userId));
        } catch (error) {
            showError('Failed to unblock user');
        }
    };

    const filteredUsers = blockedUsers.filter(u =>
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())
    );

    if (loading) return <SkeletonLoader count={3} type="list" />;

    return (
        <div className="max-w-4xl mx-auto p-6 bg-glass-card backdrop-blur-xl rounded-3xl shadow-glass border border-glass-border">
            <h2 className="text-2xl font-bold text-text-primary drop-shadow-sm mb-6 flex items-center gap-2">
                <FaUserSlash className="text-primary-pink" /> Blocked Users
            </h2>

            <div className="mb-6 relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                    type="text"
                    placeholder="Search blocked users..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-[rgba(255,255,255,0.05)] border border-glass-border rounded-xl focus:ring-2 focus:ring-primary-pink outline-none transition-all text-text-primary placeholder:text-text-muted"
                />
            </div>

            {filteredUsers.length === 0 ? (
                <div className="text-center py-12 text-text-muted">
                    <FaUserSlash size={48} className="mx-auto mb-3 opacity-20" />
                    <p>No blocked users found.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredUsers.map(user => (
                        <div key={user._id} className="flex flex-col sm:flex-row items-center justify-between p-4 rounded-2xl border border-glass-border hover:bg-[rgba(255,255,255,0.05)] transition-all gap-4 bg-[rgba(255,255,255,0.02)]">
                            <div className="flex items-center gap-4 w-full sm:w-auto sm:flex-1 overflow-hidden">
                                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[rgba(255,46,136,0.1)] flex items-center justify-center text-primary-pink font-bold text-xl border border-primary-pink/30 shadow-[0_0_15px_rgba(255,46,136,0.2)]">
                                    {user.name?.[0]?.toUpperCase() || 'U'}
                                </div>
                                <div className="overflow-hidden w-full">
                                    <h4 className="font-bold text-text-primary truncate">{user.name}</h4>
                                    <p className="text-sm text-text-secondary truncate">{user.email}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => handleUnblock(user._id)}
                                className="w-full sm:w-auto flex-shrink-0 px-5 py-2 bg-primary-pink text-white text-sm font-bold rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2 shadow-md shadow-primary-pink/20"
                            >
                                <FaUnlock size={12} /> Unblock
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default BlockedUsers;
